import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaPencilAlt } from '@react-icons/all-files/fa/FaPencilAlt';
import { FaPlus } from '@react-icons/all-files/fa/FaPlus';
import { FaTrash } from '@react-icons/all-files/fa/FaTrash';
import { FaUser } from '@react-icons/all-files/fa/FaUser';
import ProfaseModal from '../commons/ProfaseModal';

const API_USUARIOS_BUZON = '/api/buzon-tributario/usuarios';
const ADD_MODAL_Z = 110000;
const PAGE_SIZE = 10;

interface UsuarioRef {
  id: number;
  nombreCompleto?: string | null;
  nombre?: string | null;
  apellido?: string | null;
  usuario?: string | null;
  email?: string | null;
}

interface UsuarioBuzonRow {
  id: number;
  usuario?: UsuarioRef | null;
  configuracion?: { notificarNuevosMensajes?: boolean | null } | null;
}

function authHeader(): Record<string, string> {
  const token = localStorage.getItem('jhi-authenticationToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function usuarioDisplayName(u?: UsuarioRef | null): string {
  if (!u) return '—';
  const full = u.nombreCompleto?.trim();
  if (full) return full;
  const parts = `${u.nombre ?? ''} ${u.apellido ?? ''}`.trim();
  return parts || u.usuario?.trim() || u.email?.trim() || `#${u.id}`;
}

function parseUsuarios(data: unknown): UsuarioRef[] {
  const rows = Array.isArray(data)
    ? data
    : (data && typeof data === 'object' && Array.isArray((data as { data?: unknown }).data)
      ? (data as { data: unknown[] }).data
      : []);
  return rows.filter((u): u is UsuarioRef =>
    !!u && typeof u === 'object' && typeof (u as UsuarioRef).id === 'number'
  );
}

export default function BuzonTributarioUsuariosConfig({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [rows, setRows] = useState<UsuarioBuzonRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<UsuarioBuzonRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [queryInput, setQueryInput] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        size: String(PAGE_SIZE),
        sort: 'id,asc',
        cacheBuster: String(Date.now()),
      });
      if (query.trim()) params.set('query', query.trim());
      const res = await fetch(`${API_USUARIOS_BUZON}?${params.toString()}`, {
        headers: { ...authHeader() },
      });
      if (!res.ok) {
        setRows([]);
        setTotalItems(0);
        return;
      }
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
      setTotalItems(parseInt(res.headers.get('x-total-count') ?? '0', 10));
    } catch {
      setRows([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [page, query]);

  useEffect(() => {
    if (!open) {
      setAddOpen(false);
      setEditing(null);
      setQueryInput('');
      setQuery('');
      setPage(0);
      return;
    }
    void load();
  }, [open, load]);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(0);
      setQuery(queryInput);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [queryInput, open]);

  const linkedIds = useMemo(
    () => new Set(rows.map(r => r.usuario?.id).filter((id): id is number => id != null)),
    [rows],
  );

  const handleClose = () => {
    setAddOpen(false);
    setEditing(null);
    onClose();
  };

  const handleDelete = async (id: number) => {
    const res = await fetch(`${API_USUARIOS_BUZON}/${id}`, {
      method: 'DELETE',
      headers: { ...authHeader() },
    });
    if (res.ok || res.status === 204) {
      if (rows.length <= 1 && page > 0) {
        setPage(p => Math.max(0, p - 1));
        return;
      }
      await load();
    }
  };

  const handleSave = async (usuario: UsuarioRef, notificarNuevosMensajes: boolean) => {
    setSaving(true);
    try {
      if (editing) {
        const res = await fetch(`${API_USUARIOS_BUZON}/${editing.id}`, {
          method: 'PUT',
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: editing.id,
            usuario: { id: usuario.id },
            configuracion: { notificarNuevosMensajes },
          }),
        });
        if (!res.ok) return;
        setEditing(null);
      } else {
        const res = await fetch(API_USUARIOS_BUZON, {
          method: 'POST',
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            usuario: { id: usuario.id },
            configuracion: { notificarNuevosMensajes },
          }),
        });
        if (!res.ok) return;
        setAddOpen(false);
      }
      await load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ProfaseModal
        open={open}
        titleId="buzon-usuarios-config-title"
        size="wide"
      >
        <div style={hdr}>
          <h2 id="buzon-usuarios-config-title" className="profase-modal-title" style={{ margin: 0 }}>
            Configuración - Buzón Tributario
          </h2>
          <button type="button" className="profase-form-btn-secondary btn-sm" onClick={handleClose}>
            Cerrar
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              type="search"
              className="form-control"
              style={{ maxWidth: 280 }}
              value={queryInput}
              onChange={e => setQueryInput(e.target.value)}
              placeholder="Buscar por nombre o apellido…"
            />
            <button
              type="button"
              className="profase-form-btn-primary btn-sm"
              onClick={() => { setEditing(null); setAddOpen(true); }}
            >
              <FaPlus style={{ marginRight: 6 }} />
              Agregar
            </button>
          </div>
          {loading ? (
            <div style={{ color: 'var(--crm-text-secondary)', fontSize: 13, padding: '12px 0' }}>Cargando…</div>
          ) : rows.length === 0 ? (
            <div style={{ color: 'var(--crm-text-secondary)', fontSize: 13, padding: '12px 0' }}>
              {query.trim() ? 'No hay usuarios para esa búsqueda.' : 'No hay usuarios configurados.'}
            </div>
          ) : (
            <>
            <table style={tbl.table}>
              <thead>
                <tr>
                  <th style={tbl.th}>Usuario</th>
                  <th style={tbl.th}>Notificar nuevos mensajes</th>
                  <th style={{ ...tbl.th, width: 88 }} />
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr key={row.id}>
                    <td style={tbl.td}>{usuarioDisplayName(row.usuario)}</td>
                    <td style={tbl.td}>{row.configuracion?.notificarNuevosMensajes ? 'Sí' : 'No'}</td>
                    <td style={tbl.td}>
                      <div style={{ display: 'inline-flex', gap: 4 }}>
                        <button
                          type="button"
                          title="Editar"
                          aria-label={`Editar ${usuarioDisplayName(row.usuario)}`}
                          style={tbl.iconBtn}
                          onClick={() => { setAddOpen(false); setEditing(row); }}
                        >
                          <FaPencilAlt size={12} />
                        </button>
                        <button
                          type="button"
                          title="Eliminar"
                          aria-label={`Eliminar ${usuarioDisplayName(row.usuario)}`}
                          style={tbl.iconBtn}
                          onClick={() => { void handleDelete(row.id); }}
                        >
                          <FaTrash size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalItems > PAGE_SIZE && (
              <div style={tbl.pager}>
                <span style={{ fontSize: 12, color: 'var(--crm-text-secondary)' }}>
                  {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalItems)} de {totalItems}
                </span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    type="button"
                    className="profase-form-btn-secondary btn-sm"
                    disabled={page === 0}
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                  >
                    Anterior
                  </button>
                  <button
                    type="button"
                    className="profase-form-btn-secondary btn-sm"
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
            </>
          )}
        </div>
      </ProfaseModal>

      <UsuarioFormModal
        open={addOpen || editing != null}
        editing={editing}
        excludedIds={linkedIds}
        saving={saving}
        onClose={() => { setAddOpen(false); setEditing(null); }}
        onSave={handleSave}
      />
    </>
  );
}

function UsuarioFormModal({
  open,
  editing,
  excludedIds,
  saving,
  onClose,
  onSave,
}: {
  open: boolean;
  editing: UsuarioBuzonRow | null;
  excludedIds: Set<number>;
  saving: boolean;
  onClose: () => void;
  onSave: (usuario: UsuarioRef, notificarNuevosMensajes: boolean) => Promise<void>;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UsuarioRef[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<UsuarioRef | null>(null);
  const [notificar, setNotificar] = useState(true);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const lockUser = editing != null;

  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
      setSelected(null);
      setNotificar(true);
      return;
    }
    if (editing?.usuario) {
      setSelected(editing.usuario);
      setNotificar(editing.configuracion?.notificarNuevosMensajes === true);
      setQuery('');
      setResults([]);
    } else {
      setSelected(null);
      setNotificar(true);
    }
  }, [open, editing]);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    abortRef.current?.abort();
  }, []);

  useEffect(() => {
    if (!open || selected || lockUser) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (!q) {
      abortRef.current?.abort();
      setResults([]);
      setLoading(false);
      return;
    }
    debounceRef.current = setTimeout(() => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      fetch(`/api/usuarios?query=${encodeURIComponent(q)}&page=0&size=20&sort=nombre,asc`, {
        headers: { ...authHeader() },
        signal: controller.signal,
      })
        .then(async res => {
          if (!res.ok) throw new Error('search failed');
          return parseUsuarios(await res.json());
        })
        .then(rows => {
          if (!controller.signal.aborted) {
            setResults(rows.filter(u => !excludedIds.has(u.id)));
          }
        })
        .catch(() => {
          if (!controller.signal.aborted) setResults([]);
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, 250);
  }, [query, open, selected, excludedIds, lockUser]);

  return (
    <ProfaseModal
      open={open}
      titleId="buzon-usuarios-agregar-title"
      size="compact"
      zIndex={ADD_MODAL_Z}
    >
        <div style={hdr}>
          <h2 id="buzon-usuarios-agregar-title" className="profase-modal-title" style={{ margin: 0 }}>
            {lockUser ? 'Editar usuario' : 'Agregar usuario'}
          </h2>
          <button type="button" className="profase-form-btn-secondary btn-sm" onClick={onClose}>
            Cerrar
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 8 }}>
        <label style={form.label}>
          Usuario
          {selected ? (
            <div style={form.selectedUser}>
              <FaUser style={{ fontSize: 12, flexShrink: 0 }} />
              <span style={{ flex: 1 }}>{usuarioDisplayName(selected)}</span>
              {!lockUser && (
                <button
                  type="button"
                  className="profase-form-btn-secondary btn-sm"
                  onClick={() => { setSelected(null); setQuery(''); setResults([]); }}
                >
                  Cambiar
                </button>
              )}
            </div>
          ) : (
            <>
              <input
                type="text"
                className="form-control"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Buscar por nombre…"
                autoComplete="off"
              />
              {(loading || results.length > 0 || query.trim()) && (
                <div style={form.results}>
                  {loading && <div style={form.empty}>Buscando…</div>}
                  {!loading && results.length === 0 && query.trim() && (
                    <div style={form.empty}>Sin resultados</div>
                  )}
                  {results.map(u => (
                    <button
                      key={u.id}
                      type="button"
                      style={form.result}
                      onClick={() => { setSelected(u); setResults([]); }}
                    >
                      {usuarioDisplayName(u)}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </label>
        <label style={form.checkLabel}>
          <input
            type="checkbox"
            checked={notificar}
            onChange={e => setNotificar(e.target.checked)}
          />
          Notificar nuevos mensajes
        </label>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 12 }}>
        <button type="button" className="profase-form-btn-secondary btn-sm" onClick={onClose}>
          Cancelar
        </button>
        <button
          type="button"
          className="profase-form-btn-primary btn-sm"
          disabled={!selected || saving}
          onClick={() => { if (selected) void onSave(selected, notificar); }}
        >
          Guardar
        </button>
      </div>
    </ProfaseModal>
  );
}

const hdr: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
  gap: 10,
};

const tbl: Record<string, React.CSSProperties> = {
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
  },
  th: {
    textAlign: 'left',
    padding: '8px 10px',
    borderBottom: '1px solid var(--crm-border)',
    color: 'var(--crm-text-secondary)',
    fontWeight: 600,
  },
  td: {
    padding: '8px 10px',
    borderBottom: '1px solid var(--crm-border)',
    color: 'var(--crm-text-primary)',
  },
  iconBtn: {
    all: 'unset',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: 6,
    color: 'var(--crm-text-secondary)',
  },
  pager: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    paddingTop: 8,
  },
};

const form: Record<string, React.CSSProperties> = {
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--crm-text-secondary)',
    position: 'relative',
  },
  selectedUser: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 10px',
    border: '1px solid var(--crm-border)',
    borderRadius: 8,
    fontWeight: 600,
    color: 'var(--crm-text-primary)',
  },
  results: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 2,
    maxHeight: 200,
    overflowY: 'auto',
    border: '1px solid var(--crm-border)',
    borderRadius: 8,
    background: 'var(--crm-surface)',
    boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
    fontWeight: 400,
  },
  empty: {
    padding: '10px 12px',
    fontSize: 12,
    color: 'var(--crm-text-secondary)',
  },
  result: {
    display: 'block',
    width: '100%',
    padding: '8px 12px',
    border: 'none',
    background: 'transparent',
    color: 'var(--crm-text-primary)',
    fontSize: 13,
    cursor: 'pointer',
    textAlign: 'left',
  },
  checkLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--crm-text-primary)',
    cursor: 'pointer',
  },
};
